import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from 'react'
import { Alert, Form, Skeleton, Spin } from 'antd'
import { VStack, HStack } from '../../../../../ui/Stack'
import { DefaultJsonForm } from '../../../../../components/v2/default-json-form'
import { useSaveResult } from '../../hooks/useSaveResult'
import { EmployeeFormData } from '../../types/types'

interface EmployeeFormsProps {
  id: string | number
  formData: EmployeeFormData | null
  loading: boolean
  error: any
}

export interface EmployeeFormsRef {
  save: () => Promise<void>
}

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 8 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 24 },
  },
}

export const EmployeeForms = memo(
  forwardRef<EmployeeFormsRef, EmployeeFormsProps>((props: EmployeeFormsProps, ref) => {
    const { id, formData, loading, error } = props
    const [form] = Form.useForm()
    const { saveResult } = useSaveResult()
    const formRef = useRef<any>(null)
    const [localFormData, setLocalFormData] = useState<any>(null)
    const [isSaving, setIsSaving] = useState(false)

    useEffect(() => {
      if (formData?.schema) {
        // Инициализируем formData из resultObject или пустым объектом
        const initialFormData = formData.formData || {}
        formRef.current = initialFormData
        setLocalFormData(initialFormData)
        // Сбрасываем форму к исходным данным
        form.setFieldsValue(initialFormData)
      }
    }, [formData, form])

    const saveEmployee = useCallback(async () => {
      if (!formRef.current) {
        return
      }
      const idString = id === 'new' ? 'new' : String(id)
      setIsSaving(true)
      try {
        const ok = await saveResult(
          idString,
          formRef.current,
          formData?.schema || null,
          id === 'new',
        )
        if (!ok) {
          throw new Error('Save failed')
        }
      } finally {
        setIsSaving(false)
      }
    }, [id, saveResult, formData])

    // Экспортируем метод save через ref
    useImperativeHandle(ref, () => ({ save: saveEmployee }), [saveEmployee])

    const onFormDataChange = useCallback(async ({ formData: newFormData }) => {
      if (!formRef.current) {
        return
      }
      setLocalFormData(newFormData)
      formRef.current = { ...newFormData }
    }, [])

    const schema = useMemo(() => {
      const raw = formData?.schema || { type: 'object', properties: {}, required: [] }
      if (!raw.properties) return raw
      const s = JSON.parse(JSON.stringify(raw))
      // В режиме редактирования phones — массив с add/remove, каждый элемент — InputPhone
      if (s.properties?.phones) {
        s.properties.phones = {
          title: 'Номера телефонов',
          type: 'array',
          items: { type: 'string', format: 'phone' },
        }
      }
      return s
    }, [formData?.schema])

    if (error && !loading && !formData) {
      return (
        <HStack gap={'8'} className={'mapWrapper'}>
          При выполнении запроса возникла ошибка. Попробуйте перезагрузить страницу или открыть
          карточку повторно.
        </HStack>
      )
    }

    // Проверяем, что схема не пустая
    if (!schema || !schema.properties || Object.keys(schema.properties).length === 0) {
      return (
        <HStack gap={'8'} className={'mapWrapper'}>
          Нет доступных полей для отображения. Проверьте права доступа.
        </HStack>
      )
    }

    return (
      <HStack gap={'8'} max>
        <Form
          {...formItemLayout}
          layout="horizontal"
          form={form}
          scrollToFirstError={true}
          style={{ width: '100%' }}
        >
          {error && !loading ? (
            <div className={'alertWrapper'}>
              <Alert
                message="При выполнении операции возникла ошибка:"
                showIcon
                type="error"
                description={error}
              />
            </div>
          ) : null}
          {!loading ? (
            <VStack gap={'16'} max align={'stretch'} className={'employeeForm'}>
              <Spin spinning={isSaving}>
                {formData &&
                  schema &&
                  schema.properties &&
                  Object.keys(schema.properties).length > 0 && (
                    <DefaultJsonForm
                      formData={localFormData || {}}
                      schema={schema}
                      onChange={onFormDataChange}
                      orientation="horizontal"
                    />
                  )}
              </Spin>
            </VStack>
          ) : (
            <Skeleton active />
          )}
        </Form>
      </HStack>
    )
  }),
)

EmployeeForms.displayName = 'EmployeeForms'
